import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, createQuoteWithRetry, freshQuoteNumber } from '@/lib/api';
import { useAuthStore } from '@store/authStore';

/**
 * useQuoteDuplicator
 *
 * Exposes `duplicateQuote(source)` which deep-clones an existing quote into a
 * brand-new DRAFT with fresh server IDs, a fresh `quote_number`, and
 * `valid_until` reset to now + 7 days. The source quote is untouched.
 *
 * Flow:
 *   1. Generate the new quote_number + valid_until up front.
 *   2. POST a new bid for each source bid (status=DRAFT, dates reset).
 *   3. POST a new bid_option for each source option under that new bid.
 *   4. POST the new quote referencing the new bid_ids.
 *   5. (Optional) batch-copy passengers minus quote_passenger_id.
 *   6. (Optional) PATCH carry-forward files (contract / crews / additional_services).
 *
 * Returns `{ duplicateQuote, isDuplicating, currentStep }`. `currentStep`
 * reflects the user-facing label for the progress UI (or `null` when idle).
 */
export function useQuoteDuplicator() {
  const queryClient = useQueryClient();
  const { userProfile } = useAuthStore();

  const [isDuplicating, setIsDuplicating] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);

  const duplicateQuote = useCallback(async (source) => {
    if (!source) {
      return { success: false, error: new Error('No source quote provided') };
    }
    if (!userProfile?.userprofile_id || !userProfile?.org_id) {
      return { success: false, error: new Error('Current user is missing — sign in and retry') };
    }

    setIsDuplicating(true);
    setCurrentStep('Preparing duplicate…');

    const validUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    const newQuoteNumber = freshQuoteNumber();

    try {
      // ---- Bids + bid_options -------------------------------------------------
      setCurrentStep('Creating bids…');
      const sourceBidItems = Array.isArray(source.bid_items) ? source.bid_items : [];
      const newBidIds = [];

      for (let i = 0; i < sourceBidItems.length; i++) {
        const srcBid = sourceBidItems[i]?.bid;
        if (!srcBid) continue;

        // Match the working sample shape exactly. trip_number uses the short
        // `T{rand}` format the column expects (the `TR-…` form is too long);
        // message_id must be explicit `null` (omission gets coerced to ""
        // which trips a constraint); bid_number is NOT a column on `bids`
        // and was extra in the failing payload.
        const bidPayload = {
          trip_number: `T${Math.floor(Math.random() * 1000000)}${i}`,
          created_by: userProfile.userprofile_id,
          broker_id: userProfile.userprofile_id,
          broker_org_id: userProfile.org_id,
          client_name: srcBid.client_name || '',
          client_email: srcBid.client_email || '',
          client_phone: srcBid.client_phone || '',
          departure_airport: srcBid.departure_airport || '',
          arrival_airport: srcBid.arrival_airport || '',
          departure_date: srcBid.departure_date || null,
          return_date: srcBid.return_date || null,
          passenger_count: srcBid.passenger_count || 1,
          baggage_requirements: srcBid.baggage_requirements || '',
          special_requests: srcBid.special_requests || '',
          status: 'DRAFT',
          total_amount: Number(srcBid.total_amount || 0).toFixed(2),
          currency: srcBid.currency || 'USD',
          valid_until: validUntil,
          sent_at: null,
          viewed_at: null,
          accepted_at: null,
          notes: srcBid.notes || '',
          message_id: null,
          leg_itinerary: Array.isArray(srcBid.leg_itinerary) ? srcBid.leg_itinerary : [],
        };

        const newBid = await apiClient.createBid(bidPayload);
        const newBidId = newBid?.bid_id;
        if (!newBidId) {
          throw new Error('createBid response missing bid_id');
        }
        newBidIds.push(newBidId);

        const srcOptions = Array.isArray(srcBid.bid_options) ? srcBid.bid_options : [];
        if (srcOptions.length > 0) {
          setCurrentStep('Creating bid options…');
          for (const srcOpt of srcOptions) {
            // Mirror createBidOptions in useQuoteProcessor.js — explicit
            // columns, numeric fields stringified to 2dp, no spread.
            const optPayload = {
              bid_id: newBidId,
              aircraft_id: srcOpt?.aircraft_id || '',
              operator_id: srcOpt?.operator_id || '',
              base_price: Number(srcOpt?.base_price || 0).toFixed(2),
              fuel_cost: Number(srcOpt?.fuel_cost || 0).toFixed(2),
              landing_fees: Number(srcOpt?.landing_fees || 0).toFixed(2),
              crew_fees: Number(srcOpt?.crew_fees || 0).toFixed(2),
              catering_cost: Number(srcOpt?.catering_cost || 0).toFixed(2),
              ground_transport: Number(srcOpt?.ground_transport || 0).toFixed(2),
              other_fees: Number(srcOpt?.other_fees || 0).toFixed(2),
              total_price: Number(srcOpt?.total_price || 0).toFixed(2),
              broker_markup: Number(srcOpt?.broker_markup || 0).toFixed(2),
              final_price: Number(srcOpt?.final_price || 0).toFixed(2),
              federal_excise_tax: Number(srcOpt?.federal_excise_tax || 0).toFixed(2),
              segment_tax: Number(srcOpt?.segment_tax || 0).toFixed(2),
              platform_fee: Number(srcOpt?.platform_fee || 0).toFixed(2),
              operator_cost: Number(srcOpt?.operator_cost || 0).toFixed(2),
              is_recommended: !!srcOpt?.is_recommended,
              recommendation_reason: srcOpt?.recommendation_reason || '',
              availability_confirmed: srcOpt?.availability_confirmed !== false,
              shortlisted: srcOpt?.shortlisted,
              risk_level: srcOpt?.risk_level || 'LOW',
              trust_score: Number(srcOpt?.trust_score || 0).toFixed(2),
              estimated_flight_time: srcOpt?.estimated_flight_time || 0,
              aircraft_features: srcOpt?.aircraft_features || { beds: 1, wifi: false, entertainment: false },
              status: srcOpt?.status
            };
            await apiClient.createBidOption(optPayload);
          }
        }
      }

      // ---- New quote ----------------------------------------------------------
      setCurrentStep('Creating quote…');
      const quotePayload = {
        broker_org_id: userProfile.org_id,
        created_by: userProfile.userprofile_id,
        client_id: source.client_id || source.client?.client_id || null,
        departure_date: source.departure_date,
        return_date: source.return_date || null,
        passenger_count: source.passenger_count,
        quote_number: newQuoteNumber,
        departure_airport: source.departure_airport,
        arrival_airport: source.arrival_airport,
        is_round_trip: !!source.is_round_trip,
        is_multi_leg: !!source.is_multi_leg,
        note_to_client: source.note_to_client || null,
        status: 'DRAFT',
        valid_until: validUntil,
        bid_items: newBidIds.map((bid_id) => ({ bid_id })),
      };

      const newQuote = await createQuoteWithRetry(quotePayload);
      if (!newQuote?.quote_id) {
        throw new Error('createQuote response missing quote_id');
      }

      // ---- Optional: copy passengers -----------------------------------------
      const sourcePassengers = Array.isArray(source.passengers) ? source.passengers : [];
      if (sourcePassengers.length > 0) {
        setCurrentStep('Copying passengers…');
        const passengers = sourcePassengers.map((p) => {
          const { quote_passenger_id, quote_id, created_at, updated_at, ...rest } = p;
          return rest;
        });
        try {
          // Endpoint expects `{ passengers: [...] }` (wrapping is applied per-caller).
          await apiClient.createQuotePassengersBatch(newQuote.quote_id, { passengers });
        } catch (err) {
          // Non-fatal — the duplicate quote itself succeeded.
          console.warn('Failed to copy passengers to duplicated quote:', err);
        }
      }

      // ---- Optional: carry forward trip-sheet defaults ------------------------
      const srcFiles = source.files || {};
      const carry = {};
      if (srcFiles.contract) carry.contract = srcFiles.contract;
      if (srcFiles.crews) carry.crews = srcFiles.crews;
      if (srcFiles.additional_services) carry.additional_services = srcFiles.additional_services;
      // Intentionally drop srcFiles.tripsheet — stale and regenerable.
      if (Object.keys(carry).length > 0) {
        setCurrentStep('Carrying forward files…');
        try {
          await apiClient.updateQuote(newQuote.quote_id, { files: carry });
        } catch (err) {
          console.warn('Failed to carry forward files to duplicated quote:', err);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      return { success: true, data: newQuote };
    } catch (err) {
      console.error('duplicateQuote failed:', err);
      toast.error(err?.message || 'Failed to duplicate quote', { position: 'bottom-right' });
      return { success: false, error: err };
    } finally {
      setIsDuplicating(false);
      setCurrentStep(null);
    }
  }, [queryClient, userProfile]);

  return { duplicateQuote, isDuplicating, currentStep };
}
